import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  HostBinding,
  ViewChild,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { MatSidenav } from '@angular/material/sidenav';
import { Subscription } from 'rxjs';
import { filter, delay } from 'rxjs/operators';
import { ResponsiveBreakpointsService } from '../../responsive-breakpoints.service';
import { SideMenusService } from '../../side-menus/side-menus.service';

@Component({
  selector: 'app-beta-layout',
  templateUrl: './beta-layout.component.html',
  styleUrls: ['./beta-layout.component.scss']
})
export class BetaLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.layout-beta') layoutFlag = true;
  @HostBinding('class.server-side-rendered') serverSideRendered: boolean;
  @HostBinding('class.main-sidenav-opened') mainSidenavOpened: boolean;
  @HostBinding('class.alt-sidenav-opened') altSidenavOpened: boolean;

  @ViewChild('mainSidenav') mainSidenav!: MatSidenav;
  @ViewChild('altSidenav') altSidenav!: MatSidenav;

  mainSidenavSubscription?: Subscription;
  altSidenavSubscription?: Subscription;
  screenSizeChangeSubscription?: Subscription;
  changeMainMenuModeSubscription?: Subscription;
  routeChangeSubscription?: Subscription;
  private safariShellSubscription?: Subscription;
  private pageShowHandler?: () => void;

  mainSideMenuMode: string;
  isMultichatRoute = false;
  isCompareModeRoute = false;
  showClarityAndBusinessMenus = false;
  showDoubleCoachMenu = false;
  currentScreenSize = 'large';
  isMaxWidth1000px = false;
  /** Kept for host/CSS parity; mirrors `useMainMenuOnSafari` from `SideMenusService`. */
  isSafari = false;
  /** Driven by root `SideMenusService` + `[hidden]` so the main menu is not destroyed on navigation. */
  useMainMenuOnSafari = false;

  private mediaQuery?: MediaQueryList;
  private mediaQueryListener?: (event: MediaQueryListEvent) => void;

  constructor(
    private sideMenusService: SideMenusService,
    private responsiveBreakpointsService: ResponsiveBreakpointsService,
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.serverSideRendered = isPlatformServer(this.platformId);
    this.mainSidenavOpened = true;
    this.altSidenavOpened = false;
    this.mainSideMenuMode = 'side';
    this.currentScreenSize = this.responsiveBreakpointsService.currentScreenSize;

    if (isPlatformBrowser(this.platformId)) {
      this.isMaxWidth1000px = window.matchMedia('(max-width: 1000px)').matches;
      this.sideMenusService.recomputeSafariBetaShellMainMenu(this.router.url);
      this.syncSafariShellFromService();
    }
  }

  private syncSafariShellFromService(): void {
    const v = this.sideMenusService.safariBetaShellUsesMainMenu;
    this.useMainMenuOnSafari = v;
    this.isSafari = v;
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.safariShellSubscription = this.sideMenusService.safariBetaShellUsesMainMenu$.subscribe((v) => {
        this.useMainMenuOnSafari = v;
        this.isSafari = v;
        this.cdr.detectChanges();
      });
    }

    this.updateMultichatRoute(this.router.url);

    this.routeChangeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (isPlatformBrowser(this.platformId)) {
          this.ngZone.run(() => {
            this.sideMenusService.recomputeSafariBetaShellMainMenu(this.router.url);
            this.syncSafariShellFromService();
            this.cdr.detectChanges();
          });
        }
        this.updateMultichatRoute((event as NavigationEnd).urlAfterRedirects);
      });

    this.screenSizeChangeSubscription = this.responsiveBreakpointsService.screenSizeChangeSubject
      .pipe(delay(0))
      .subscribe((event) => {
        this.currentScreenSize = event;

        if (event === 'small' || event === 'medium') {
          this.sideMenusService.changeMainMenuModeSubject.next('push');
        }

        if (event === 'large') {
          this.sideMenusService.changeMainMenuModeSubject.next('side');
        }
      });

    if (isPlatformBrowser(this.platformId)) {
      this.mediaQuery = window.matchMedia('(max-width: 1000px)');
      this.mediaQueryListener = (event: MediaQueryListEvent) => {
        this.isMaxWidth1000px = event.matches;
      };
      this.mediaQuery.addEventListener('change', this.mediaQueryListener);

      this.pageShowHandler = () => {
        this.ngZone.run(() => {
          this.sideMenusService.recomputeSafariBetaShellMainMenu(this.router.url);
          this.syncSafariShellFromService();
          this.cdr.detectChanges();
        });
      };
      window.addEventListener('pageshow', this.pageShowHandler);
    }
  }

  ngAfterViewInit(): void {
    if (!this.mainSidenav.opened) {
      this.mainSidenav.open();
    }

    if (isPlatformBrowser(this.platformId)) {
      queueMicrotask(() => {
        this.ngZone.run(() => {
          this.sideMenusService.recomputeSafariBetaShellMainMenu(this.router.url);
          this.syncSafariShellFromService();
          this.cdr.detectChanges();
        });
      });
    }

    this.mainSidenavSubscription = this.sideMenusService.toggleMainMenuSubject.subscribe(
      (event) => {
        if (event === 'open') {
          this.mainSidenav.open();
        }
      },
      (error) => {
        console.log('toggleMainMenu [Layout Beta] - error', error);
      }
    );

    this.altSidenavSubscription = this.sideMenusService.toggleAltMenuSubject.subscribe(
      (event) => {
        switch (event) {
          case 'toggle':
            this.altSidenav.toggle();
            break;
          case 'open':
            this.altSidenav.open();
            break;
          case 'close':
            this.altSidenav.close();
            break;
          default:
            this.altSidenav.toggle();
        }
      },
      (error) => {
        console.log('toggleAltMenu [Layout Beta] - error', error);
      }
    );

    this.changeMainMenuModeSubscription = this.sideMenusService.changeMainMenuModeSubject.subscribe(
      (menuMode) => {
        this.mainSideMenuMode = menuMode;
      },
      (error) => {
        console.log('changeMainMenuModeSubject [Layout Beta] - error', error);
      }
    );
  }

  ngOnDestroy(): void {
    this.mainSidenavSubscription?.unsubscribe();
    this.altSidenavSubscription?.unsubscribe();
    this.screenSizeChangeSubscription?.unsubscribe();
    this.changeMainMenuModeSubscription?.unsubscribe();
    this.routeChangeSubscription?.unsubscribe();
    this.safariShellSubscription?.unsubscribe();

    if (this.mediaQuery && this.mediaQueryListener) {
      this.mediaQuery.removeEventListener('change', this.mediaQueryListener);
    }

    if (isPlatformBrowser(this.platformId) && this.pageShowHandler) {
      window.removeEventListener('pageshow', this.pageShowHandler);
      this.pageShowHandler = undefined;
    }
  }

  openedStart(): void {}

  mainSidenavToggled(opened: boolean): void {
    if (!opened && this.mainSidenav) {
      setTimeout(() => {
        if (this.mainSidenav && !this.mainSidenav.opened) {
          this.mainSidenav.open();
        }
      }, 0);
    }

    this.mainSidenavOpened = true;
    this.sideMenusService.setMainMenuState(true);
  }

  altSidenavToggled(opened: boolean): void {
    this.altSidenavOpened = opened;
  }

  onMainMenuOpenChange(opened: boolean): void {
    this.mainSidenavOpened = opened;
    this.sideMenusService.setMainMenuState(opened);
  }

  private updateMultichatRoute(url: string): void {
    this.isMultichatRoute =
      url.includes('/multi-ai/executive-chat') ||
      url.includes('/multi-ai/exective-chat');
    this.isCompareModeRoute = url.includes('/ai/compare-mode');

    const coachMenuAllowedRoutes = ['/ai/clarity', '/ai/clarichat', '/ai/compare-mode', '/search-history'];
    const isCoachMenuAllowedRoute = coachMenuAllowedRoutes.some((route) => url.includes(route));

    this.showClarityAndBusinessMenus = isCoachMenuAllowedRoute;
    this.showDoubleCoachMenu = url.includes('/ai/compare-mode');
  }
}
